import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import {
  CreateUserParams,
  DeleteUserByIdResponse,
  GetUserByIdResponse, GetUserRequestParams,
  GetUserResponse, UpdateUserRequestParams,
} from '../../interfaces/users.interface';
import { environment } from '../../../environments/environment';
import { toQueryString } from '../../shared/helpers/helpers';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private apiUrl = environment.api + '/users';
  private tokenKey = environment.accessTokenName;

  constructor(private http: HttpClient) {}

  private handleResponse<T>(response: T & { code: number; message?: string }): T {
    if (response.code !== 0) {
      throw new Error(response.message);
    }
    return response;
  }

  private handleError(error: any): Observable<never> {
    console.error('API Error:', error);
    return throwError(() => new Error(error.error?.message || 'Error'));
  }

  getUserService(requestParams: GetUserRequestParams): Observable<GetUserResponse> {
    const url = this.apiUrl + '?' + toQueryString(requestParams);
    return this.http.get<GetUserResponse>(url).pipe(
      tap(response => this.handleResponse(response)), // ✅ Pakai arrow function
      catchError(error => this.handleError(error))   // ✅ Pakai arrow function
    );
  }

  getUserById(id: string): Observable<GetUserByIdResponse> {
    return this.http.get<GetUserByIdResponse>(`${this.apiUrl}/${id}`).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  deleteUserById(id: string): Observable<DeleteUserByIdResponse> {
    return this.http.delete<DeleteUserByIdResponse>(`${this.apiUrl}/${id}`).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  createUser(userData: CreateUserParams): Observable<GetUserByIdResponse> {
    return this.http.post<GetUserByIdResponse>(this.apiUrl, userData).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  updateUser(id: string, userData: UpdateUserRequestParams): Observable<GetUserByIdResponse> {
    return this.http.put<GetUserByIdResponse>(`${this.apiUrl}/${id}`, userData).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }

  getMe(): Observable<GetUserByIdResponse> {
    return this.http.get<GetUserByIdResponse>(`${this.apiUrl}/me`).pipe(
      tap(response => this.handleResponse(response)),
      catchError(error => this.handleError(error))
    );
  }
}
